import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  #requestRevision = 0;

  /** @type {Map<string, { promise: Promise<string>, createdAt: number }>} */
  #prefetchedSections = new Map();

  #rugPrefetchRevision = 0;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  #variantChangedHandler = (event) => this.variantChanged(event);

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.#variantChangedHandler);
    this.addEventListener('pointerover', this.#handleRugPrefetchIntent);
    this.addEventListener('focusin', this.#handleRugPrefetchIntent);
    this.#resizeObserver.observe(this);
    this.#scheduleRugSectionPrefetch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#requestRevision++;
    this.#rugPrefetchRevision++;
    this.#abortController?.abort();
    this.#prefetchedSections.clear();
    this.removeEventListener('change', this.#variantChangedHandler);
    this.removeEventListener('pointerover', this.#handleRugPrefetchIntent);
    this.removeEventListener('focusin', this.#handleRugPrefetchIntent);
    this.#resizeObserver.disconnect();
  }

  #handleRugPrefetchIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const option = event.target.matches('input[data-rug-preview-url]')
      ? event.target
      : event.target.closest('label')?.querySelector('input[data-rug-preview-url]');

    if (option instanceof HTMLInputElement) {
      void this.#prefetchRugOption(option, 'high')?.catch(() => undefined);
    }
  };

  #scheduleRugSectionPrefetch(afterInteraction = false) {
    if (this.dataset.templateProductMatch !== 'true') return;
    if (!this.querySelector('fieldset[data-rug-image-swatches="true"]')) return;

    const revision = ++this.#rugPrefetchRevision;
    const schedule = () => {
      const prefetch = () => {
        if (revision === this.#rugPrefetchRevision && this.isConnected) {
          void this.#prefetchRugSections(revision);
        }
      };

      if (afterInteraction) {
        window.setTimeout(prefetch, 100);
      } else if ('requestIdleCallback' in window) {
        window.requestIdleCallback(prefetch, { timeout: 2000 });
      } else {
        window.setTimeout(prefetch, 500);
      }
    };

    if (document.readyState === 'complete') {
      schedule();
    } else {
      window.addEventListener('load', schedule, { once: true });
    }
  }

  async #prefetchRugSections(revision) {
    const fieldset = this.querySelector('fieldset[data-rug-image-swatches="true"]');
    if (!fieldset) return;
    const alternatives = Array.from(fieldset.querySelectorAll('input[data-option-value-id][data-option-available="true"]:not(:checked)')).slice(0, 2);

    for (const option of alternatives) {
      if (revision !== this.#rugPrefetchRevision || !this.isConnected) return;
      try {
        await this.#prefetchRugOption(option);
      } catch {
        continue;
      }
    }
  }

  #prefetchRugOption(option, priority = 'low') {
    if (this.dataset.templateProductMatch !== 'true') return;
    if (!(option instanceof HTMLInputElement) || option.checked || option.dataset.optionAvailable !== 'true') return;
    if (!option.closest('fieldset[data-rug-image-swatches="true"]') || option.dataset.connectedProductUrl) return;

    const sectionId = this.closest('.shopify-section')?.id.replace(/^shopify-section-/, '');
    const position = Number.parseInt(option.dataset.inputId?.split('-')[0] ?? '', 10) - 1;
    const selectedValues = this.selectedOptionsValues;
    if (!sectionId || !selectedValues[position] || !option.dataset.optionValueId) return;

    const optionValues = [...selectedValues];
    optionValues[position] = option.dataset.optionValueId;
    const requestUrl = this.buildRequestUrl(option, 'rug-prefetch', optionValues, sectionId);
    const cached = this.#prefetchedSections.get(requestUrl);
    if (cached && Date.now() - cached.createdAt < 60000) return cached.promise;

    for (const [url, entry] of this.#prefetchedSections) {
      if (Date.now() - entry.createdAt >= 60000) this.#prefetchedSections.delete(url);
    }
    while (this.#prefetchedSections.size >= 6) {
      this.#prefetchedSections.delete(this.#prefetchedSections.keys().next().value);
    }

    const promise = fetch(requestUrl, { priority }).then((response) => {
      if (!response.ok) throw new Error(`Could not prefetch product section: ${response.status}`);
      return response.text();
    });
    const entry = { promise, createdAt: Date.now() };
    this.#prefetchedSections.set(requestUrl, entry);
    void promise.then((html) => {
      if (this.isConnected) this.#warmPrefetchedRugImages(html);
    }).catch(() => {
      if (this.#prefetchedSections.get(requestUrl) === entry) this.#prefetchedSections.delete(requestUrl);
    });
    return promise;
  }

  #warmPrefetchedRugImages(responseText) {
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    const images = Array.from(html.querySelectorAll('media-gallery .media-gallery__grid img.product-media__image')).slice(0, 2);

    for (const source of images) {
      const image = new Image();
      image.decoding = 'async';
      image.fetchPriority = 'low';
      image.sizes = source.getAttribute('sizes') ?? '';
      image.srcset = source.getAttribute('srcset') ?? '';
      image.src = source.getAttribute('src') ?? '';
      void image.decode().catch(() => undefined);
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const sectionId = isOnProductPage && !loadsNewProduct
      ? this.closest('.shopify-section')?.id.replace(/^shopify-section-/, '')
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption, null, [], sectionId), morphElementSelector);

  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @param {string} [sectionId] - The product section to re-render without fetching the rest of the page.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = [], sectionId) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && (!source || source === 'rug-prefetch')) {
      const optionValues = source === 'rug-prefetch' ? sourceSelectedOptionsValues : this.selectedOptionsValues;
      params.push(`option_values=${optionValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'card-size-variant-picker': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    if (sectionId) params.push(`section_id=${encodeURIComponent(sectionId)}`);

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;
    const revision = ++this.#requestRevision;
    const cached = this.#prefetchedSections.get(requestUrl);
    const cachedResponse = cached && Date.now() - cached.createdAt < 60000 ? cached.promise : undefined;
    const fetchResponse = () => fetch(requestUrl, { signal: controller.signal }).then((response) => {
      if (!response.ok) throw new Error(`Could not update product variant: ${response.status}`);
      return response.text();
    });

    (cachedResponse ? cachedResponse.catch(fetchResponse) : fetchResponse())
      .then((responseText) => {
        if (revision !== this.#requestRevision || !this.isConnected) return;
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const responseRoot = morphElementSelector === 'main' ? html.querySelector('main') : html;
        const responsePickers = Array.from(responseRoot?.querySelectorAll('variant-picker') ?? []);
        const targetPath = new URL(requestUrl, window.location.href).pathname;
        const targetPickers = morphElementSelector === 'main' ? responsePickers.filter((picker) =>
          picker.dataset.templateProductMatch === 'true' && picker.dataset.productUrl &&
          new URL(picker.dataset.productUrl, window.location.href).pathname === targetPath
        ) : [];
        const responsePicker = responsePickers.find((picker) =>
          this.dataset.sectionId && this.dataset.blockId &&
          picker.dataset.sectionId === this.dataset.sectionId && picker.dataset.blockId === this.dataset.blockId
        ) ?? (targetPickers.length === 1 ? targetPickers[0] : undefined) ??
          (responsePickers.length === 1 ? responsePickers[0] : undefined);
        const textContent = responsePicker?.querySelector('script[type="application/json"]')?.textContent;
        if (!textContent) throw new Error('Updated variant picker was missing from the response');
        const variant = JSON.parse(textContent);
        const isOnProductPage = this.dataset.templateProductMatch === 'true' &&
          !this.closest('product-card, quick-add-dialog, featured-product-information');

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          // Card controls are siblings of the response's JSON-only variant-picker.
          // Give each card subclass the full response so it can find its own form.
          const isCardPicker = this.tagName === 'CARD-SIZE-VARIANT-PICKER' ||
            this.tagName === 'SWATCHES-VARIANT-PICKER-COMPONENT';
          newProduct = this.updateVariantPicker(isCardPicker ? html : responsePicker);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(variant, this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }

        if (isOnProductPage) {
          const url = new URL(window.location.href);
          const productUrl = responsePicker?.dataset.productUrl;
          if (morphElementSelector === 'main' && productUrl) {
            url.pathname = new URL(productUrl, url).pathname;
          }
          if (variant?.id) url.searchParams.set('variant', variant.id);
          else url.searchParams.delete('variant');
          if (url.href !== window.location.href) history.replaceState({}, '', url.toString());
        }
        this.#scheduleRugSectionPrefetch(true);
      })
      .catch((error) => {
        if (revision !== this.#requestRevision) return;
        this.#pendingRequestUrl = undefined;
        this.dispatchEvent(new CustomEvent('variant:request-failed', {
          bubbles: true,
          detail: { productId: this.dataset.productId ?? '' },
        }));
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml instanceof HTMLElement && newHtml.matches('variant-picker')
      ? newHtml
      : newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    selectedOptions.sort((a, b) => {
      const position = (option) => Number.parseInt(option.dataset.inputId?.split('-')[0] ?? '', 10);
      const aPosition = position(a);
      const bPosition = position(b);
      return Number.isFinite(aPosition) && Number.isFinite(bPosition) ? aPosition - bPosition : 0;
    });

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
